import { gql } from '@apollo/client';
import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { t } from 'i18next';
import { useCallback, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { ModalCloseButton } from '~components/ui/ModalCloseButton';
import { ConfigurationStatus } from '~graphql/__generated__/types';
import { fromError } from '~utils/errors';
import { logger } from '~utils/logger';
import { useDuplicateDisplayConfigurationMutation } from './__generated__/CreateConfigurationModal.graphql';
import { ConfigurationStateToggle, ConfigurationStatusType } from './ConfigStateToggle';

const CloneFormData = z.object({
  name: z.string().min(1),
  description: z.string().optional(),
  state: z.nativeEnum(ConfigurationStatus),
});
type CloneFormData = z.infer<typeof CloneFormData>;

export enum CreateType {
  Clone = 'clone',
}

interface Props {
  isOpen: boolean;
  onCancel: () => void;
  onSuccess?: (id: string) => Promise<void> | void;
  configurationState?: ConfigurationStatus;
  configurationId?: string;
  customerId: string;
  createType: CreateType;
  defaultDescription?: string;
  defaultName?: string;
  onDraftStatusSelect?: (
    status:
      | ConfigurationStatus.Draft
      | ConfigurationStatus.Published
      | ConfigurationStatus.Readytopublish,
  ) => void;
}

export function CreateConfigurationModal({
  isOpen,
  onCancel,
  onSuccess,
  defaultDescription,
  defaultName,
  customerId,
  configurationState,
  configurationId,
  onDraftStatusSelect,
}: Props) {
  const toast = useToast();
  const {
    getFieldState,
    setValue,
    register,
    watch,
    handleSubmit,
    formState: { errors, isSubmitting, isDirty, isValid },
  } = useForm<CloneFormData>({
    defaultValues: {
      name: defaultName,
      description: defaultDescription,
      state: configurationState || ConfigurationStatus.Unpublished,
    },
    resolver: zodResolver(CloneFormData),
    mode: 'onChange',
  });
  const initialFocusRef = useRef<HTMLInputElement | null>(null);
  const { ref: nameInputRef, ...nameInputProps } = register('name');

  const [cloneConfiguration] = useDuplicateDisplayConfigurationMutation({
    refetchQueries: ['CustomerConfigurationsPage'],
    awaitRefetchQueries: true,
  });

  const handleClone = useCallback(
    async ({ name, description, state }: CloneFormData) => {
      if (!configurationId) {
        logger.error(
          `Both configuration id and state are required to clone config. Received configurationId: ${configurationId}, state: ${state}`,
        );
        toast({
          status: 'error',
          title: t('createConfigurationErr'),
          description: t('createConfigurationErrDesc'),
        });
        return;
      }
      try {
        const { data } = await cloneConfiguration({
          variables: {
            configurationId,
            name,
            description,
            customerId,
          },
        });

        if (!data || !data.cloneDisplayConfiguration.configuration) {
          throw new Error('Clone configuration failed.');
        }
        await onSuccess?.(data.cloneDisplayConfiguration.configuration.id);
        toast({
          status: 'success',
          title: t('createConfiguration'),
          description: t('createConfigurationSuccess'),
        });
      } catch (err) {
        toast({
          status: 'error',
          title: t('createConfigurationErr'),
          description: fromError(err, 'CloneConfiguration', {
            CONFIGURATION_DUPLICATE_NAME: t('duplicatenameerr'),
          }),
        });
      } finally {
        onCancel();
      }
    },
    [cloneConfiguration, configurationId, onCancel, onSuccess, toast, customerId],
  );

  useEffect(() => {
    const nameDirty = getFieldState('name').isDirty;
    const descriptionDirty = getFieldState('description').isDirty;
    const stateDirty = getFieldState('state').isDirty;

    if (!nameDirty) {
      setValue('name', defaultName || '', { shouldDirty: false });
    }

    if (!descriptionDirty) {
      setValue('description', defaultDescription || '', { shouldDirty: false });
    }

    if (!stateDirty) {
      setValue('state', configurationState || ConfigurationStatus.Unpublished, {
        shouldDirty: false,
      });
    }
  }, [defaultName, defaultDescription, configurationState, getFieldState, setValue]);

  const onConfigurationStateChange = (state: ConfigurationStatusType) => {
    setValue('state', state, { shouldDirty: true });
    onDraftStatusSelect?.(state);
  };

  return (
    <Modal initialFocusRef={initialFocusRef} isOpen={isOpen} onClose={onCancel} size={'3xl'}>
      <form onSubmit={handleSubmit(handleClone)}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('createConfiguration')}</ModalHeader>
          <ModalCloseButton onClick={onCancel} />
          <ModalBody>
            <VStack align={'start'} gap={2}>
              {configurationState === ConfigurationStatus.Published && onDraftStatusSelect && (
                <ConfigurationStateToggle
                  value={watch('state') as ConfigurationStatusType}
                  onStateChange={onConfigurationStateChange}
                />
              )}
              <FormControl isRequired isInvalid={Boolean(errors.name)}>
                <FormLabel>{t('configurationName')}</FormLabel>
                <Input
                  ref={(r) => {
                    nameInputRef(r);
                    initialFocusRef.current = r;
                  }}
                  placeholder={t('configurationNamePlaceholder')}
                  {...nameInputProps}
                />
                <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
              </FormControl>
              <FormControl isInvalid={Boolean(errors.description)}>
                <FormLabel>{t('description')}</FormLabel>
                <Input
                  type="textarea"
                  placeholder={t('configurationDescPlaceholder')}
                  {...register('description')}
                />
                <FormErrorMessage>{errors.description?.message}</FormErrorMessage>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" colorScheme="blue" onClick={onCancel} isDisabled={isSubmitting}>
              {t('close')}
            </Button>
            <Button
              variant="solid"
              colorScheme="blue"
              marginLeft="3"
              type="submit"
              isDisabled={(!isValid && !isDirty) || isSubmitting || Object.keys(errors).length > 0}
              isLoading={isSubmitting}
            >
              {t('create')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </form>
    </Modal>
  );
}

CreateConfigurationModal.graphql = {
  mutations: {
    DuplicateDisplayConfiguration: gql`
      mutation DuplicateDisplayConfiguration(
        $configurationId: String!
        $name: String!
        $description: String
        $customerId: String!
      ) {
        cloneDisplayConfiguration(
          configurationId: $configurationId
          name: $name
          description: $description
          customerId: $customerId
        ) {
          configuration {
            ...ConfigurationTable_configuration
          }
        }
      }
    `,
  },
};
